import React from 'react';
import { BookOpen, Quote, Sparkles } from 'lucide-react';
import { WisdomShlok } from '../types';

interface ShlokCardProps {
  shlok: WisdomShlok;
}

const ShlokCard: React.FC<ShlokCardProps> = ({ shlok }) => {
  return (
    <div className="h-full bg-white rounded-3xl border border-[#4C3B8A]/10 shadow-sm hover:shadow-xl transition-all duration-500 group">
      <div className="p-8 flex flex-col h-full">
        {/* Source & Icon */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-[#4C3B8A]/5 text-[#4C3B8A]">
              <BookOpen size={20} />
            </div>
            <div>
              <h4 className="font-serif font-black text-[#4C3B8A] leading-tight">{shlok.source_text}</h4>
              <p className="text-[10px] font-bold text-[#4C3B8A]/50 uppercase tracking-widest">{shlok.chapter_verse}</p>
            </div>
          </div>
          <div className="opacity-10 group-hover:opacity-20 transition-opacity">
            <Quote size={32} className="text-[#4C3B8A]" />
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1">
          {shlok.sanskrit_devanagari ? (
            <>
              {/* Sanskrit Devanagari */}
              <div className="mb-6">
                <p className="text-xl md:text-2xl font-serif text-gray-800 leading-relaxed text-center italic">
                  "{shlok.sanskrit_devanagari}"
                </p>
              </div>

              {/* Transliteration */}
              <div className="mb-8 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <p className="text-xs text-gray-500 text-center italic font-medium leading-relaxed">
                  {shlok.transliteration}
                </p>
              </div>

              {/* English Translation */}
              <div className="mb-8">
                <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3 text-center">English Translation</h5>
                <p className="text-gray-700 text-lg leading-relaxed text-center font-medium">
                  {shlok.english_translation}
                </p>
              </div>
            </>
          ) : (
            /* Live RAG Content (Combined) */
            <div className="mb-8 p-6 bg-slate-50/50 rounded-3xl border border-indigo-100/30">
              <p className="text-gray-800 text-lg leading-relaxed text-center font-serif italic whitespace-pre-wrap">
                {shlok.content}
              </p>
            </div>
          )}
        </div>

        {/* Life Guidance */}
        {shlok.life_guidance && (
          <div className="mt-8 pt-8 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-amber-500" />
              <h5 className="text-[11px] font-black text-[#4C3B8A] uppercase tracking-widest">Nani's Life Guidance</h5>
            </div>
            <div className="p-5 rounded-2xl bg-gradient-to-br from-[#4C3B8A]/5 to-indigo-50 border border-indigo-100/50">
              <p className="text-[#4C3B8A] leading-relaxed text-sm font-medium">
                {shlok.life_guidance}
              </p>
            </div>
          </div>
        )}

        {/* Tags */}
        {shlok.tags && shlok.tags.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            {shlok.tags.map((tag, idx) => (
              <span key={idx} className="px-3 py-1 rounded-full bg-white border border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShlokCard;
